/**
 * MapTab — India map with school markers, region/state click, and counts.
 *
 * Renders identically to the original inline JSX in school.tsx.
 */

import React, { useMemo } from 'react';
import IndiaMap from '../IndiaMap';
import type { UseFiltersReturn } from '../../hooks/useFilters';
import type { UseKPIDataReturn } from '../../hooks/useKPIData';

interface MapTabProps {
  filters: UseFiltersReturn;
  kpiData: UseKPIDataReturn;
  mapMode: 'state' | 'region';
  setMapMode: (mode: 'state' | 'region') => void;
  showDistricts: boolean;
}

export default function MapTab({ filters, kpiData, mapMode, setMapMode, showDistricts }: MapTabProps) {
  const { state, region, category, kpi, globalFilters, setState, setRegion } = filters;
  const {
    categoryList, kpiList,
    kpiDataLoading, kpiDataError,
    aggregatedStateData, aggregatedRegionData,
    filteredStatesList, kpiSchoolCoordinates,
    selectedAreaCounts,
  } = kpiData;

  // When advanced filters are active, derive legend counts and choropleth data directly
  // from kpiSchoolCoordinates (which is already filter-aware) so they match tabular results.
  const filtersActive = globalFilters.length > 0 && kpiSchoolCoordinates.length > 0;

  const displayTotal = filtersActive ? kpiSchoolCoordinates.length : selectedAreaCounts.totalKVs;
  const displayPMShree = useMemo(
    () => filtersActive ? kpiSchoolCoordinates.filter(s => Number(s.isPMShree) > 0).length : selectedAreaCounts.pmShree,
    [filtersActive, kpiSchoolCoordinates, selectedAreaCounts.pmShree]
  );

  // Regroup filtered coordinates by state or region for the choropleth
  const filteredRegionData = useMemo(() => {
    if (!filtersActive || !category || !kpi) return null;
    const counts = new Map<string, number>();
    kpiSchoolCoordinates.forEach(s => {
      const name = mapMode === 'region' ? (s.roName || '') : (s.stateName || '');
      if (name) counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([name, value]) =>
      mapMode === 'region' ? { name, value, region: name } : { name, value, state: name }
    );
  }, [filtersActive, category, kpi, kpiSchoolCoordinates, mapMode]);

  return (
    <div className="w-full min-h-[800px] flex flex-col">
      {/* Info bar */}
      <div className="flex justify-between items-center mb-4 px-4">
        <div className="flex gap-3 flex-col items-end">
          {kpiDataLoading && <span className="text-sm text-gray-500">Loading filtered data...</span>}
          {!category && !kpi && !state && !region && (
            <span className="text-sm text-gray-500 py-2">Showing all records</span>
          )}
          {category && kpi && (
            <span className="text-sm text-green-600">
              Filtered by: {categoryList.find(c => c.key === category)?.label} - {kpiList.find(k => k.key === kpi)?.label}
            </span>
          )}
          {(state || region) && (
            <span className="text-sm text-blue-600">
              {state && `State: ${state}`}
              {state && region && ' | '}
              {region && `Region: ${region}`}
            </span>
          )}
        </div>
      </div>

      {/* Error */}
      {kpiDataError && (
        <div className="mb-4 mx-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{kpiDataError}</p>
        </div>
      )}

      {/* No records */}
      {!kpiDataLoading && !kpiDataError && category && kpi && globalFilters.length > 0 && kpiSchoolCoordinates.length === 0 && (
        <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-lg mx-4 mb-4">
          <div className="text-center p-12">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No schools match the current filter criteria.</h3>
            <p className="text-sm text-gray-400">Try adjusting your filters to see results.</p>
          </div>
        </div>
      )}

      {/* Map */}
      {(!category || !kpi || !globalFilters.length || kpiSchoolCoordinates.length > 0 || kpiDataLoading) && (
        <div className="flex-1">
          <IndiaMap
            geoJsonUrl="/data/india-states.geojson"
            districtGeoJsonUrl="/data/india-districts.geojson"
            showDistricts={showDistricts}
            mapMode={mapMode}
            onModeChange={setMapMode}
            regionData={
              filteredRegionData
                ? filteredRegionData
                : category && kpi && aggregatedStateData.length > 0
                ? mapMode === 'state'
                  ? aggregatedStateData.map(s => ({ name: s.stateName, value: s.schoolCount, state: s.stateName }))
                  : aggregatedRegionData.map(r => ({ name: r.roName, value: r.schoolCount, region: r.roName }))
                : mapMode === 'state'
                  ? filteredStatesList.map(s => ({ name: s.stateName, value: s.schoolCount, state: s.stateName }))
                  : []
            }
            filters={{
              state: state || undefined,
              region: region || undefined,
              category: category || undefined,
              kpi: kpi || undefined,
              kpiLabel: kpi ? kpiList.find((o: any) => o.key === kpi)?.label : undefined,
            }}
            selectedRegion={mapMode === 'region' ? region : state}
            schoolCoordinates={kpiSchoolCoordinates}
            totalKVCount={displayTotal}
            pmShreeCount={displayPMShree}
            onRegionClick={(regionData) => {
              if (mapMode === 'region') {
                if (regionData.region || regionData.name) {
                  setRegion(regionData.region || regionData.name || '');
                  setState('');
                } else if (!regionData.name) {
                  setRegion('');
                  setState('');
                }
              } else {
                if (regionData.state && !regionData.region) {
                  setState(regionData.name || '');
                  setRegion('');
                } else if (regionData.region) {
                  setRegion(regionData.name || '');
                } else if (!regionData.name) {
                  setState('');
                  setRegion('');
                }
              }
            }}
          />
        </div>
      )}
    </div>
  );
}
